/**
 * VIVA ARTIST — label de production tous médias (audio, vidéo, image, immersif).
 *
 * Présentation contemplative (thème du passeur de lumière et du souffle sculpté)
 * suivie d'un formulaire de candidature. Inspiré dans la structure de la page
 * Sirene Records (maisondelasirene.fr/sirene_records), mais avec la voix propre
 * du Prieuré : l'art comme vibration d'éternité, l'artiste comme passeur.
 */
import { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import SeoHead from "@/components/SeoHead";
import Ornament from "@/components/Ornament";

const COPY = {
  fr: {
    hero: "VIVA ARTIST",
    tagline: "Label de production · Tous médias",

    manifesto_title: "Manifeste",
    manifesto: [
      "VIVA ARTIST est le label de production du Prieuré saint Léonard. Il accueille des artistes de toutes disciplines — musique, chant, image, film, installation, performance — pour donner corps, souffle et rayonnement à leurs projets.",
      "Nous croyons que l'artiste est un passeur de lumière. Celui qui sculpte le silence pour en faire naître le chant. Celui qui sculpte la lumière et permet l'ombre — car sans ombre, la lumière n'a rien à révéler. Celui qui sculpte le souffle pour nous faire vibrer d'éternité et de joie.",
      "Au cœur de la baie du Mont-Saint-Michel, dans ce lieu millénaire où la pierre porte encore la prière et la mer répond aux astres, chaque œuvre créée avec nous devient une offrande : un fragment de beauté déposé dans le temps.",
    ],

    vision_title: "Une vision",
    vision: [
      { icon: "◈", title: "L'atelier long", body: "Nous prenons le temps qu'il faut. Un projet n'est pas un produit ; c'est un accouchement lent. Résidences, retraites, allers-retours entre l'atelier et le silence." },
      { icon: "◇", title: "L'artisanat du son et de l'image", body: "Prise de son au diapason du lieu, chef opérateur en lumière naturelle, montage à la main, mixage sur matériel choisi. Rien de standard, tout d'incarné." },
      { icon: "✦", title: "L'accompagnement fraternel", body: "Direction artistique, dramaturgie, écriture, mais aussi hébergement en résidence, tables partagées, soutien humain. L'œuvre naît d'une communauté." },
      { icon: "✧", title: "La diffusion large", body: "Édition physique et numérique, distribution en salles, plateformes, festivals. Et surtout : rencontres avec le public, en concert, en projection, en installation vivante." },
    ],

    disciplines_title: "Nos disciplines",
    disciplines: [
      { code: "audio", label: "Audio", detail: "Musique de chambre, chant sacré, jazz d'inspiration, création sonore, radio d'auteur, podcast." },
      { code: "video", label: "Vidéo", detail: "Court et long métrage, documentaire de création, captation d'œuvre, portrait d'artiste." },
      { code: "image", label: "Image", detail: "Photographie d'art, séries au long cours, livres d'artiste, éditions imprimées." },
      { code: "immersive", label: "Immersif", detail: "Installations sonores et visuelles, performances rituelles, expériences en espaces patrimoniaux." },
    ],

    form_title: "Vous êtes artiste ?",
    form_intro: "Si votre projet cherche un lieu, un souffle, une main pour l'accompagner — parlez-nous. Nous lisons chaque candidature. La forme importe peu ; ce sont l'intention et le désir qui nous parlent.",

    // Field labels
    first_name: "Prénom",
    last_name: "Nom",
    artist_name: "Nom d'artiste (facultatif)",
    email: "Courriel",
    phone: "Téléphone",
    country: "Pays",
    discipline: "Discipline principale",
    medium: "Médium précis (chant, cinéma, installation…)",
    project_title: "Titre du projet",
    project_pitch: "Présentez votre projet (10 à 4 000 caractères)",
    project_url: "Lien vers un extrait ou un dossier (Vimeo, SoundCloud, Drive, portfolio…)",
    stage: "Où en est le projet ?",
    stage_options: {
      idea: "Idée / intuition",
      writing: "Écriture / composition",
      preproduction: "Préproduction",
      production: "En cours de production",
      postproduction: "Postproduction",
      distribution: "Prêt à diffuser",
    },
    desired_support: "Accompagnement souhaité",
    support_options: {
      recording: "Enregistrement (studio, lieu)",
      filming: "Tournage",
      editing: "Montage",
      mixing: "Mixage",
      mastering: "Mastering",
      direction_artistique: "Direction artistique",
      edition: "Édition (physique ou numérique)",
      diffusion: "Diffusion / distribution",
      immersive_design: "Conception immersive / scénographie",
      other: "Autre",
    },
    consent_newsletter: "Je souhaite recevoir les nouvelles du label (résidences, appels à projets)",
    notes: "Autre chose à nous dire ?",

    // PDF book (optional)
    portfolio_label: "Dossier PDF complet (facultatif · 10 Mo max)",
    portfolio_hint: "Vous pouvez joindre un book, un dossier de presse, une note d'intention, une partition… Un seul fichier PDF, 10 Mo maximum.",
    portfolio_selected: "Fichier sélectionné :",
    portfolio_uploading: "Envoi du dossier PDF…",
    portfolio_too_large: "Le fichier dépasse 10 Mo. Compressez-le ou envoyez-nous un lien.",
    portfolio_only_pdf: "Seul le format PDF est accepté.",
    portfolio_uploaded: "✓ Dossier PDF envoyé",

    submit: "Confier mon projet",
    submitting: "Envoi…",
    success_title: "Merci — votre voix est entendue",
    success_body: "Nous lirons votre candidature avec attention et vous répondrons dans les prochains jours. Que la lumière vous accompagne d'ici là.",
    error: "Une erreur est survenue — merci de réessayer.",

    footer_line: "Que ce que vous portez trouve ici son écho.",
  },

  en: {
    hero: "VIVA ARTIST",
    tagline: "Production label · All media",

    manifesto_title: "Manifesto",
    manifesto: [
      "VIVA ARTIST is the production label of the Priory of Saint Leonard. It welcomes artists of every discipline — music, song, image, film, installation, performance — to give body, breath and radiance to their work.",
      "We believe that the artist is a bearer of light. The one who carves silence to birth song. The one who carves light and permits shadow — for without shadow, light has nothing to reveal. The one who carves breath to make us tremble with eternity and joy.",
      "At the heart of the Mont-Saint-Michel bay, in this millennial place where stone still carries prayer and the sea answers the stars, each work created with us becomes an offering: a shard of beauty laid down in time.",
    ],

    vision_title: "A vision",
    vision: [
      { icon: "◈", title: "The long workshop", body: "We take the time that is needed. A project is not a product; it is a slow birth. Residencies, retreats, comings and goings between studio and silence." },
      { icon: "◇", title: "Craft of sound and image", body: "Recording tuned to the room, cinematography in natural light, editing by hand, mixing on chosen gear. Nothing standard, everything embodied." },
      { icon: "✦", title: "Fraternal accompaniment", body: "Artistic direction, dramaturgy, writing, but also lodging in residency, shared tables, human support. The work is born of a community." },
      { icon: "✧", title: "Wide diffusion", body: "Physical and digital editions, distribution in venues, platforms, festivals. And above all: encounters with the public, in concert, in projection, in living installation." },
    ],

    disciplines_title: "Our disciplines",
    disciplines: [
      { code: "audio", label: "Audio", detail: "Chamber music, sacred song, inspired jazz, sound creation, radio, podcast." },
      { code: "video", label: "Video", detail: "Short and feature films, creative documentary, work capture, artist portrait." },
      { code: "image", label: "Image", detail: "Fine-art photography, long-running series, artist books, printed editions." },
      { code: "immersive", label: "Immersive", detail: "Sound and visual installations, ritual performances, experiences in heritage spaces." },
    ],

    form_title: "Are you an artist?",
    form_intro: "If your project seeks a place, a breath, a hand to walk with — speak to us. We read every submission. The form matters little; it is the intention and the longing that speak.",

    first_name: "First name",
    last_name: "Last name",
    artist_name: "Artist name (optional)",
    email: "Email",
    phone: "Phone",
    country: "Country",
    discipline: "Main discipline",
    medium: "Precise medium (song, cinema, installation…)",
    project_title: "Project title",
    project_pitch: "Introduce your project (10 to 4,000 characters)",
    project_url: "Link to an excerpt or a folder (Vimeo, SoundCloud, Drive, portfolio…)",
    stage: "Where is the project?",
    stage_options: {
      idea: "Idea / intuition",
      writing: "Writing / composing",
      preproduction: "Pre-production",
      production: "In production",
      postproduction: "Post-production",
      distribution: "Ready to release",
    },
    desired_support: "Support wished for",
    support_options: {
      recording: "Recording (studio, location)",
      filming: "Filming",
      editing: "Editing",
      mixing: "Mixing",
      mastering: "Mastering",
      direction_artistique: "Artistic direction",
      edition: "Edition (physical or digital)",
      diffusion: "Diffusion / distribution",
      immersive_design: "Immersive design / scenography",
      other: "Other",
    },
    consent_newsletter: "I would like to receive news from the label (residencies, calls for projects)",
    notes: "Anything else to tell us?",

    portfolio_label: "Full PDF portfolio (optional · 10 MB max)",
    portfolio_hint: "You may attach a book, press kit, statement of intent, score… A single PDF file, 10 MB maximum.",
    portfolio_selected: "Selected file:",
    portfolio_uploading: "Uploading portfolio…",
    portfolio_too_large: "File exceeds 10 MB. Please compress it or send us a link.",
    portfolio_only_pdf: "Only PDF format is accepted.",
    portfolio_uploaded: "✓ Portfolio uploaded",

    submit: "Entrust my project",
    submitting: "Sending…",
    success_title: "Thank you — your voice is heard",
    success_body: "We will read your submission with care and respond in the coming days. May light walk with you until then.",
    error: "An error occurred — please try again.",

    footer_line: "May what you carry find its echo here.",
  },
};

const INITIAL_FORM = {
  first_name: "", last_name: "", artist_name: "",
  email: "", phone: "", country: "FR",
  discipline: "audio", medium: "",
  project_title: "", project_pitch: "",
  project_url: "", stage_of_project: "idea",
  desired_support: [],
  consent_newsletter: false, notes: "",
};

export default function VivaArtist() {
  const { locale } = useI18n();
  const t = COPY[locale === "fr" ? "fr" : "en"];
  const [form, setForm] = useState(INITIAL_FORM);
  const [pdfFile, setPdfFile] = useState(null);   // File | null
  const [pdfError, setPdfError] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleSupport = (code) => {
    setForm((f) => ({
      ...f,
      desired_support: f.desired_support.includes(code)
        ? f.desired_support.filter((c) => c !== code)
        : [...f.desired_support, code],
    }));
  };

  const onPdfChange = (e) => {
    setPdfError("");
    const file = e.target.files?.[0] || null;
    if (!file) { setPdfFile(null); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setPdfError(t.portfolio_only_pdf);
      setPdfFile(null);
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError(t.portfolio_too_large);
      setPdfFile(null);
      e.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      // Step 1 — create the candidacy
      const { data } = await api.post("/viva-artist/submissions", form);
      // Step 2 — if a PDF was attached, upload it inline
      if (pdfFile && data?.id) {
        const fd = new FormData();
        fd.append("file", pdfFile);
        try {
          await api.post(
            `/viva-artist/submissions/${data.id}/portfolio`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        } catch (uploadErr) {
          // The candidacy is created — surface a soft warning but still show success
          console.warn("Portfolio upload failed:", uploadErr);
        }
      }
      setOk(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setMsg(err?.response?.data?.detail || t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="viva-artist" style={{ background: "var(--parchment)" }}>
      <SeoHead
        titles={{ fr: "Viva Artist — Label de production du Prieuré", en: "Viva Artist — The Priory's production label" }}
        descriptions={{
          fr: "Viva Artist, label de production tous médias du Prieuré saint Léonard : musique, cinéma, image et créations immersives. Résidences d'artistes en baie du Mont-Saint-Michel, au service de la beauté et du patrimoine normand.",
          en: "Viva Artist, the all-media production label of the Priory of Saint Léonard: music, cinema, image and immersive creations. Artist residencies on the Bay of Mont-Saint-Michel, serving beauty and Norman heritage.",
        }}
        keywords="Viva Artist, label de production, artistes, résidence d'artistes, cinéma, musique sacrée, beauté, création immersive, art sacré, Normandie, baie du Mont-Saint-Michel"
      />
      {/* HERO — image cinématographique (rayon de lumière sculptant l'ombre) */}
      <section
        className="relative overflow-hidden border-b"
        style={{ borderColor: "var(--gold)" }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('/photos/viva-artist-hero-1600.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
          aria-hidden="true"
        />
        {/* Voile sombre pour lisibilité, plus intense en bas */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,16,12,0.35) 0%, rgba(20,16,12,0.55) 60%, rgba(20,16,12,0.75) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 py-24 sm:py-32 lg:py-40 px-6 text-center">
          <Ornament light />
          <p
            className="font-body uppercase text-xs tracking-[0.4em] mt-6"
            style={{ color: "#d4af37" }}
            data-testid="viva-tagline"
          >
            {t.tagline}
          </p>
          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 tracking-wide"
            style={{
              color: "#f5f0e4",
              textShadow: "0 2px 24px rgba(0,0,0,0.6)",
            }}
            data-testid="viva-hero"
          >
            {t.hero}
          </h1>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <p
          className="font-body uppercase text-xs tracking-[0.3em] text-center mb-6"
          style={{ color: "var(--gold-dark, #a68b3d)" }}
        >
          {t.manifesto_title}
        </p>
        <div className="space-y-5">
          {t.manifesto.map((para, i) => (
            <p
              key={i}
              className="font-body text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--ink)" }}
              data-testid={`viva-manifesto-${i}`}
            >
              {para}
            </p>
          ))}
        </div>
      </section>

      {/* VISION — 4 pillars */}
      <section
        className="py-16 px-6 border-t border-b"
        style={{
          borderColor: "var(--gold)",
          background: "linear-gradient(180deg, var(--parchment) 0%, rgba(212,175,55,0.04) 100%)",
        }}
      >
        <div className="max-w-5xl mx-auto">
          <h2
            className="font-heading text-3xl text-center mb-10"
            style={{ color: "var(--ink)" }}
          >
            {t.vision_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.vision.map((v, i) => (
              <div
                key={i}
                className="bg-white border p-6 sm:p-8"
                style={{ borderColor: "var(--gold)" }}
                data-testid={`viva-vision-${i}`}
              >
                <div
                  className="font-heading text-3xl mb-3"
                  style={{ color: "var(--gold-dark, #a68b3d)" }}
                >
                  {v.icon}
                </div>
                <h3
                  className="font-heading text-xl mb-2"
                  style={{ color: "var(--ink)" }}
                >
                  {v.title}
                </h3>
                <p
                  className="font-body text-base leading-relaxed"
                  style={{ color: "var(--muted)" }}
                >
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2
          className="font-heading text-3xl text-center mb-10"
          style={{ color: "var(--ink)" }}
        >
          {t.disciplines_title}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.disciplines.map((d) => (
            <div
              key={d.code}
              className="border p-5 text-center"
              style={{ borderColor: "var(--border)" }}
              data-testid={`viva-discipline-${d.code}`}
            >
              <h3
                className="font-heading text-lg mb-2 uppercase tracking-wider"
                style={{ color: "var(--ink)" }}
              >
                {d.label}
              </h3>
              <p
                className="font-body text-sm leading-relaxed"
                style={{ color: "var(--muted)" }}
              >
                {d.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section
        className="py-16 px-6 border-t"
        style={{ borderColor: "var(--gold)", background: "#faf7ef" }}
      >
        <div className="max-w-2xl mx-auto">
          {ok ? (
            <div
              className="bg-white border p-10 text-center"
              style={{ borderColor: "var(--gold)" }}
              data-testid="viva-success"
            >
              <div
                className="font-heading text-4xl mb-4"
                style={{ color: "var(--gold-dark, #a68b3d)" }}
              >
                ✧
              </div>
              <h2
                className="font-heading text-2xl mb-3"
                style={{ color: "var(--ink)" }}
              >
                {t.success_title}
              </h2>
              <p
                className="font-body text-base leading-relaxed"
                style={{ color: "var(--muted)" }}
              >
                {t.success_body}
              </p>
            </div>
          ) : (
            <>
              <h2
                className="font-heading text-3xl text-center mb-4"
                style={{ color: "var(--ink)" }}
              >
                {t.form_title}
              </h2>
              <p
                className="font-body italic text-base text-center mb-10 max-w-xl mx-auto"
                style={{ color: "var(--muted)" }}
              >
                {t.form_intro}
              </p>

              <form
                onSubmit={submit}
                className="bg-white border p-6 sm:p-8 space-y-5"
                style={{ borderColor: "var(--border)" }}
                data-testid="viva-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t.first_name} required value={form.first_name} onChange={upd("first_name")} testid="viva-first-name" />
                  <Field label={t.last_name} required value={form.last_name} onChange={upd("last_name")} testid="viva-last-name" />
                  <Field label={t.artist_name} value={form.artist_name} onChange={upd("artist_name")} testid="viva-artist-name" />
                  <Field label={t.email} type="email" required value={form.email} onChange={upd("email")} testid="viva-email" />
                  <Field label={t.phone} value={form.phone} onChange={upd("phone")} testid="viva-phone" />
                  <Field label={t.country} value={form.country} onChange={upd("country")} testid="viva-country" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SelectField
                    label={t.discipline}
                    value={form.discipline}
                    onChange={upd("discipline")}
                    testid="viva-discipline"
                    options={t.disciplines.map((d) => ({ value: d.code, label: d.label }))}
                  />
                  <Field label={t.medium} value={form.medium} onChange={upd("medium")} testid="viva-medium" />
                </div>

                <Field
                  label={t.project_title} required
                  value={form.project_title} onChange={upd("project_title")}
                  testid="viva-project-title"
                />

                <TextArea
                  label={t.project_pitch} required rows={6}
                  minLength={10} maxLength={4000}
                  value={form.project_pitch} onChange={upd("project_pitch")}
                  testid="viva-project-pitch"
                />

                <Field
                  label={t.project_url} type="url"
                  value={form.project_url} onChange={upd("project_url")}
                  testid="viva-project-url"
                />

                <SelectField
                  label={t.stage}
                  value={form.stage_of_project}
                  onChange={upd("stage_of_project")}
                  testid="viva-stage"
                  options={Object.entries(t.stage_options).map(([v, l]) => ({ value: v, label: l }))}
                />

                <fieldset>
                  <legend
                    className="block text-xs font-body uppercase tracking-widest mb-2"
                    style={{ color: "var(--muted)" }}
                  >
                    {t.desired_support}
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(t.support_options).map(([code, label]) => (
                      <label
                        key={code}
                        className="flex items-center gap-2 font-body text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          data-testid={`viva-support-${code}`}
                          checked={form.desired_support.includes(code)}
                          onChange={() => toggleSupport(code)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <TextArea
                  label={t.notes} rows={3}
                  value={form.notes} onChange={upd("notes")}
                  testid="viva-notes"
                />

                {/* PDF portfolio (optional) — public upload window 15 min */}
                <div
                  className="border p-4"
                  style={{ borderColor: "var(--gold)", background: "rgba(212,175,55,0.06)" }}
                >
                  <span
                    className="block text-xs font-body uppercase tracking-widest mb-2"
                    style={{ color: "var(--gold-dark, #a68b3d)" }}
                  >
                    {t.portfolio_label}
                  </span>
                  <p
                    className="font-body text-xs italic mb-3"
                    style={{ color: "var(--muted)" }}
                  >
                    {t.portfolio_hint}
                  </p>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={onPdfChange}
                    data-testid="viva-portfolio-input"
                    className="font-body text-sm"
                  />
                  {pdfFile && (
                    <p
                      className="font-body text-sm mt-2"
                      style={{ color: "var(--ink)" }}
                      data-testid="viva-portfolio-selected"
                    >
                      {t.portfolio_selected}{" "}
                      <strong>{pdfFile.name}</strong>{" "}
                      <span style={{ color: "var(--muted)" }}>
                        ({(pdfFile.size / 1024 / 1024).toFixed(2)} Mo)
                      </span>
                    </p>
                  )}
                  {pdfError && (
                    <p
                      className="font-body text-sm mt-2"
                      style={{ color: "var(--cinnabar)" }}
                      data-testid="viva-portfolio-error"
                    >
                      {pdfError}
                    </p>
                  )}
                </div>

                <label className="flex items-start gap-2 font-body text-sm" style={{ color: "var(--muted)" }}>
                  <input
                    type="checkbox"
                    data-testid="viva-consent"
                    checked={form.consent_newsletter}
                    onChange={upd("consent_newsletter")}
                    className="mt-1"
                  />
                  <span>{t.consent_newsletter}</span>
                </label>

                <button
                  type="submit"
                  disabled={busy}
                  className="btn btn-primary w-full sm:w-auto"
                  data-testid="viva-submit"
                >
                  {busy ? (pdfFile ? t.portfolio_uploading : t.submitting) : t.submit}
                </button>

                {msg && (
                  <p
                    className="text-sm"
                    style={{ color: "var(--cinnabar)" }}
                    data-testid="viva-error"
                  >
                    {msg}
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </section>

      {/* FOOTER LINE */}
      <section className="py-12 px-6 text-center">
        <Ornament />
        <p
          className="font-body italic text-base sm:text-lg mt-4 max-w-xl mx-auto"
          style={{ color: "var(--muted)" }}
        >
          {t.footer_line}
        </p>
      </section>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span
        className="block text-xs font-body uppercase tracking-widest mb-1"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </span>
      <input
        data-testid={testid}
        className="w-full px-3 py-2 border font-body"
        style={{ borderColor: "var(--border)" }}
        {...rest}
      />
    </label>
  );
}

function TextArea({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span
        className="block text-xs font-body uppercase tracking-widest mb-1"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </span>
      <textarea
        data-testid={testid}
        className="w-full px-3 py-2 border font-body"
        style={{ borderColor: "var(--border)" }}
        {...rest}
      />
    </label>
  );
}

function SelectField({ label, testid, options, ...rest }) {
  return (
    <label className="block">
      <span
        className="block text-xs font-body uppercase tracking-widest mb-1"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </span>
      <select
        data-testid={testid}
        className="w-full px-3 py-2 border font-body bg-white"
        style={{ borderColor: "var(--border)" }}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
